import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createExternalResource,
    selectResourceCategoryById,
    createExternalResourceCategory,
    removeExternalResourceCategory,
    editExternalResource,
    getExternalResources,
    removeExternalResource,
    selectAllExternalResources,
    selectAllResourceCategories,
    toggleCategoryManager,
    editExternalResourceCategory
} from "../../state/externalResourcesSlice";
import {useDebounceCallback} from '@react-hook/debounce'
import {slidingEditDistance} from '../../util/util'
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import useApiStatusDispatch from "../../hooks/useApiStatusDispatch";
import useS3DirectUpload from '../../hooks/useS3DirectUpload'
import Card from "react-bootstrap/Card";
import classes from "./Resources.module.css"
import useCurrentUser from "../../hooks/useCurrentUser";
import Select from 'react-select'
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import {useHistory} from 'react-router-dom'
import Spinner from "react-bootstrap/Spinner";
import MultiSelect from "react-multi-select-component";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Tooltip from "react-bootstrap/Tooltip"
import DiscussResourceButton from '../DiscussResourceButton/DiscussResourceButton'
// title, description, link, category, and video length

const CategoryManager = ()=>{
    const dispatch = useDispatch()
    const show = useSelector(state=>state.externalResources.categoryManager.show)
    const categories = useSelector(selectAllResourceCategories)

    const onHide = ()=>{
        dispatch(
            toggleCategoryManager()
        )
    }

    const handleDelete = (id)=>{
        if (!window.confirm('Are you sure you want to delete this category?')) return
        dispatch(
            removeExternalResourceCategory({id})
        )
    }

    const dispatchEdit = (payload)=>{
        dispatch(
            editExternalResourceCategory(payload)
        )
    }

    const [newCategoryName,setNewCategoryName] = useState('')
    const [isSaving,setIsSaving] = useState(false)
    const handleAdd = ()=>{
        setIsSaving(true)
        dispatch(
            createExternalResourceCategory(
                {category_name:newCategoryName}
            )
        ).then(()=>{
            setIsSaving(false)
            setNewCategoryName('')
        })
    }

    const debouncedEdit = useDebounceCallback(dispatchEdit,500)

    return (
        <Modal 
            onHide={onHide} 
            show={show}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Manage Resource Categories
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <strong>Create Category</strong>
                <div className="d-flex mb-1">
                    <Form.Control 
                        onChange={e=>{
                            setNewCategoryName(e.target.value)
                        }}
                        placeholder="New Category" 
                        value={newCategoryName}
                    />
                    <Button 
                        disabled={isSaving}
                        onClick={handleAdd}
                        className="ml-1" 
                        variant="outline-success"
                    >
                        {
                            isSaving
                            ? <Spinner animation="border" size="sm"/>
                            : <i className="fa fa-plus"/>
                        }
                    </Button>
                </div>
                <hr className="my-2" />
                <strong>Edit Categories</strong>
                {categories.map(category=>(
                    <div className="d-flex mt-1">
                        <Form.Control 
                            defaultValue={category.category_name}
                            onChange={ e => {
                                debouncedEdit({
                                    id:category.id,
                                    update:{category_name:e.target.value}
                                })
                            }}
                        />
                        <Button 
                            onClick={()=>{
                                handleDelete(category.id)
                            }}
                            className="ml-1" 
                            variant="outline-danger"
                        >
                            <i className="fa fa-trash"/>
                        </Button>
                    </div>
                ))}
            </Modal.Body>
        </Modal>
    )
}

const CategoryName = ({id})=>{
    const category = useSelector(state=>selectResourceCategoryById(state,id))
    if (!Boolean(category)) return null
    return category.category_name
}

function ExternalResources() {
    const history = useHistory()
    const urlParams = new URLSearchParams(window.location.search)
    const [filterId,setFilterId] = useState(parseInt(urlParams.get('resource_id')))
    const user = useCurrentUser()

    const isClient = user.account_type === "client";
    const isGym = user.account_type === "gym";
    const isChain = user.account_type === 'chain'
    const isTrainer = user.account_type === 'trainer'

    const partOfGym = parseInt(user.gym_id) !== 0

    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [itemToEdit, setItemToEdit] = useState({});
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [searchFilter, setSearchFilter] = useState("");

    let resources = useSelector(selectAllExternalResources)
    let categoryOptions = useSelector(selectAllResourceCategories)
    let isResourcesLoading = useSelector(state => state.externalResources.isLoading);

    const isOwner = (
        partOfGym ?
            parseInt(user.gym) === 1 :
            parseInt(user.trainer) === 1
    )

    const dispatch = useDispatch()
    const statusDispatch = useApiStatusDispatch()

    useEffect(() => {
        dispatch(getExternalResources());
    }, [])

    function handleEditResource(item) {
        setShowEditModal(true);
        setItemToEdit(item)
    }

    function handleDeleteResource(id) {
        if (!window.confirm('Are you sure you want to delete this resource?')) return 

        statusDispatch(removeExternalResource(
            {
                id
            }
        ), "resource", "delete")
    }


    //map from user_id_number to user_name and account type
    const ownerMap = useMemo(() => {
        const map = {}
        resources.forEach(resource => {
            map[resource.owner_id] = {
                owner_type: resource.owner_type,
                user_name: resource.owner_name
            }
        })
        return map
    }, [resources]);


    const mapCategoriesToSelect = useMemo(() => {
        return categoryOptions.map(item => {
            return { value: item.id, label: item.category_name }
        })
    }, [categoryOptions])

    const ownerMapToSelectOptions = () => (
        Object.entries(ownerMap)
            .map(
                ([id, accountData]) => ({
                    value: id,
                    label: (
                        id === user.user_id_number ?
                            'My Resources' :
                            `${accountData.user_name}'s resources`
                    )//end ternary
                })//end cb
            )//end map
    )//end ownerMapToSelectOptions


    //for clients default to top level account type
    //else default to current user's account type
    const [ownerFilter, setOwnerFilter] = useState(
        user?.account_type === 'gym' || isChain 
        ? { value: user?.user_id_number, label: 'My Resources' } 
        : { value: 'all', label: 'All Resources' }
    )
   
    // ids of categoryFilter items
    const categoryValues = useMemo(()=>
        (categoryFilter.map(f => f.value)
    ),[categoryFilter])

    // labels of categoryFilter items
    const selectedCategories = useMemo(()=>
        (categoryFilter.map(f => f.label)
    ),[categoryFilter])


    const categoryMatch = (item) => {
        return selectedCategories.length > 0 ?
            selectedCategories.includes(item.category_name)
            :
            //if no filters true by default
            true
    }

    const ownerMatch = (item) => (
        (ownerFilter.value === "all" ||
            Number(ownerFilter.value) === Number(item.owner_id)) ?
            true :
            false
    )

    const sortComparer = (a,b) => {

        // what resource keys to use
        let resourceKeys = ['title', 'description']
        const aVal = Math.min(
            ...resourceKeys.map(
                (key,i)=>{
                    const {dist,offset} = slidingEditDistance(a[key]?.toLowerCase(),searchFilter)
                    return dist+offset+i
                }
            )
        )

        const bVal = Math.min(
            ...resourceKeys.map(
                (key,i)=>{
                    const {dist,offset} = slidingEditDistance(b[key]?.toLowerCase(),searchFilter)
                    return dist+offset+i
                }
            )
        )

        return aVal - bVal
    }

    const urlFilterMatch = (item)=>!Boolean(filterId)||item.id===filterId

    function filterItem(item) {
        return (
            categoryMatch(item) &&
            ownerMatch(item) &&
            urlFilterMatch(item)
        )
    }

    function clearFilters() {
        setCategoryFilter([]);
    }

    const checkCategoryPermission = () => {
        // if not trainer associated with gym or is Gym can always delete category
        return ( (isTrainer && !partOfGym) || isGym ?  true : false)
    
    }

    function handleCategoryDelete(category){
        if (window.confirm("Are you sure you want to delete this category for all resources?")){
            statusDispatch(removeExternalResourceCategory(
                {
                    id : category.value
                }
            ), "category", "delete")
        }
    }

    

    function handleCategoryFilters(category){

        const categoryValues = categoryFilter.map(f => f.value)

        if (categoryValues.includes(category.value)) {
            setCategoryFilter(categoryFilter.filter(item => item.value !== category.value))
        } else {
            setCategoryFilter([...categoryFilter, category])
        }
    }
    console.log(categoryFilter, "filter")





    
    const [showDelete, setShowDelete] = useState(false);

    const filterPopover = ( 
        <Popover id="category-filters" >
            <Popover.Content>
                <div>
                    {isOwner && <OverlayTrigger
                        placement="right"
                        overlay={
                            <Tooltip>
                                 Category Options
                            </Tooltip>
                        }>
                        <div className=" ml-1 float-right">
                            <i style={{ "cursor": "pointer" }}
                                onClick={() => dispatch(toggleCategoryManager())}
                                className="fas fa-cog" />
                        </div>
                    </OverlayTrigger>}
                    {mapCategoriesToSelect.length > 0 ?
                        mapCategoriesToSelect.map(category => (
                            <div style={{ display: "inline-block", position: "relative" }}>
                                {showDelete &&
                                    <span 
                                        onClick={() => { handleCategoryDelete(category) }} 
                                        className={classes.categoryDelete}
                                    > 
                                        x
                                    </span>
                                }
                                <span 
                                    style={{ cursor: "pointer" }}
                                    className={[
                                        classes.category,
                                        categoryValues.includes(category.value) ? classes.Active : ''
                                    ].join(' ')}
                                    onClick={() => {
                                        handleCategoryFilters(category);

                                    }}>
                                    {category.label}
                                </span>
                            </div>

                        ))
                        :
                        <div>No Categories found</div>
                    }
                </div>
            </Popover.Content>
        </Popover>
    )


    return (
        <div className="pt-2 px-2 px-md-3">
            <div clasName="">
                <strong className="pt-4"> 
                    Resources 
                </strong>
            </div>
            <div className="d-flex mt-3 mb-1">
                <Form.Control
                    size="sm"
                    className="mr-2"
                    placeholder="search resources"
                    value={searchFilter}
                    onChange={(e) => {
                        setSearchFilter(e.target.value)
                    }}
                />
                <ButtonGroup style={{flex:'0 0 auto'}}>
                    <OverlayTrigger
                        trigger="click"
                        placement="bottom"
                        overlay={filterPopover}
                        rootCloseEvent="click"
                        rootClose={true}
                    >
                        <Button
                            
                            size="sm"
                            variant={"outline-primary"}>
                            <i className={"fas fa-filter"} /> 
                            <span className="d-none d-sm-inline ml-1">Categories</span>
                        </Button>
                    </OverlayTrigger>
                    {isOwner&&
                        <OverlayTrigger overlay={
                            <Tooltip>Edit Categories/Filters</Tooltip>
                        }>
                            <Button 
                                onClick={()=>{
                                    dispatch(toggleCategoryManager())
                                }}
                                variant="outline-primary"
                                size="sm"
                            >
                                Edit 
                            </Button>
                        </OverlayTrigger>
                    }
                </ButtonGroup>
                {!isClient &&
                    <Button 
                        onClick={() => setShowAddModal(true)}
                        style={{flex:'0 0 auto'}}
                        variant="outline-success" 
                        className="ml-2"
                        size="sm"
                    >
                        <i className="fa fa-plus"/> Add <span className="d-none d-sm-inline">Resource</span>
                    </Button>
                }
            </div>


            {(((!isClient) && partOfGym && !isGym) || isChain) &&
                <div className="my-2 mr-1" style={{ maxWidth: '300px' }}>
                    <Select
                        options={[
                            { value: 'all', label: 'All Resources' },
                            ...ownerMapToSelectOptions(ownerMap)
                        ]}
                        onChange={selectedOption => {
                            setOwnerFilter(selectedOption)
                        }}
                        value={ownerFilter}
                    />
                </div>
            }


            {selectedCategories.length > 0 &&
                <div className="">

                    <div className="mt-2">
                        <span>{`Results filtered by  - Category : ${selectedCategories.join(' | ')}`}</span>
                    </div>
                </div>
            }


            

            {isResourcesLoading ?
                <div className="d-flex flex-wrap mt-3">
                    <Spinner className="mt-4" animation="border" role="status" variant="primary">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
                :
                <>
                    {Boolean(filterId)&&
                        <div className="my-2">
                            Viewing filtered list: 
                            <Button 
                                onClick={()=>{ 
                                    setFilterId(false)
                                    history.replace('/resources/')
                                }}
                                variant="outline-danger" 
                                className="ml-2"
                                size="sm"
                            >
                                Clear Filters
                            </Button>
                        </div>
                    }
                    <div className={classes.Grid}>
                        {resources.filter(filterItem).sort(sortComparer).map(item => {
                            return (
                                <div key={item.id} className="p-1 px-2 rounded border overflow-hidden d-flex flex-column align-items-stretch">
                                    {Boolean(item.link)&&
                                        <a target="_blank" href={item.link}>
                                             <i className="fa fa-link"/> {item.title}
                                        </a>
                                    }
                                    {!Boolean(item.link)&&
                                        item.title
                                    }

                                    {item.description && <div>
                                        <small className="d-block">{item.description}</small>
                                    </div>}
                                    {item.video_length && <div>
                                        <hr className="my-1"/>
                                        <small className="d-block">
                                            Video Length: {item.video_length}
                                        </small>
                                    </div>}
                                    <div className="mt-auto text-right">
                                        {!isClient &&
                                            item.owner_id === Number(user?.user_id_number) &&
                                            <>
                                                <Button size="sm" variant="outline-danger"
                                                    className="mr-1"
                                                    onClick={() => handleDeleteResource(item.id)}>
                                                    <i className="fa fa-trash"/>
                                                </Button>
                                                <Button 
                                                    className="mr-1"
                                                    size="sm" variant="outline-primary"
                                                    onClick={() => handleEditResource(item)}>
                                                    <i className="fa fa-edit"/>
                                                </Button>
                                            </>
                                        }
                                        <OverlayTrigger rootClose overlay={
                                            <Tooltip>Discuss this resource in the chat</Tooltip>
                                        }>
                                            <span className="ml-auto">
                                                <DiscussResourceButton
                                                    className="btn btn-sm btn-outline-info"
                                                    pendingText=""
                                                    name={item.title}
                                                    id={item.id}
                                                    type="external_resource"
                                                >
                                                    <i className="fa fa-comments"/>
                                                </DiscussResourceButton>
                                            </span>
                                        </OverlayTrigger>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </>
            }
            <CategoryManager />
            <AddExternalResourceModal
                showAddModal={showAddModal}
                setShowAddModal={setShowAddModal} />

            <EditExternalResourceModal
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                itemToEdit={itemToEdit}
                setItemToEdit={setItemToEdit}
            />

        </div>
    )
}


// Creatable Select used to create/edit categories
function CategorySelect({
    selectedCategory,
    setSelectedCategory }) {

    const [isCategoryCreateLoading, setIsCategoryCreateLoading] = useState(false);

    const user = useCurrentUser()

    const isClient = user.account_type === "client";
    const isGym = user.account_type === "gym";
    const isChain = user.account_type === 'chain'
    const partOfGym = parseInt(user.gym_id) !== 0

    const isOwner = (
        partOfGym ?
            parseInt(user.gym) === 1 :
            parseInt(user.trainer) === 1
    )

    const statusDispatch = useApiStatusDispatch();

    let categoryOptions = useSelector(selectAllResourceCategories)

    const mapCategoriesToSelect = useMemo(() => {
        return categoryOptions.map(item => {
            return { value: item.id, label: item.category_name }
        })
    }, [categoryOptions])

    function handleCreateCategory(category_name) {
        setIsCategoryCreateLoading(true);

        statusDispatch(createExternalResourceCategory(
            {
                category_name
            }
        ), "category", "create").then(res => {
            let { success, data } = res.payload;
            if (success === "1") {
                let { category_name, id } = data.external_resource_category;
                setSelectedCategory({ value: id, label: category_name })
            }
            setIsCategoryCreateLoading(false)
        })
    }

    const MenuList = ({ children, ...props }) => {
        return (
            <components.MenuList {...props}>
                <div >
                    <i className="ml-2 m-0 pt-3 fa fa-info-circle">  </i> <span><i> To create a new Category, enter a unique name above </i> </span>
                    <hr />


                </div>
                {children}
            </components.MenuList>

        );
    };


    return (
        isOwner ?
            <CreatableSelect
                onBlur={e=>{
                    handleCreateCategory(e.target.value)
                }}
                isClearable
                value={selectedCategory}
                options={mapCategoriesToSelect}
                onChange={(selected) => {
                    setSelectedCategory(selected)
                }}
                components={{ MenuList }}
                onCreateOption={handleCreateCategory}
                isLoading={isCategoryCreateLoading}
            /> :
            <Select
                isClearable
                value={selectedCategory}
                options={mapCategoriesToSelect}
                onChange={(selected) => {
                    setSelectedCategory(selected)
                }}
                isLoading={isCategoryCreateLoading}
            />
    )
}

// ********** EDIT RESOURCE MODAL ***************

function EditExternalResourceModal({
    showEditModal,
    setShowEditModal,
    itemToEdit,
    setItemToEdit }) {

    // edit modal state
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editLink, setEditLink] = useState("");
    const [editVideoLength, setEditVideoLength] = useState("");
    const [useFile, setUseFile] = useState(false)
    const [file,setFile] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)




    const statusDispatch = useApiStatusDispatch();
    let categoryOptions = useSelector(selectAllResourceCategories)

    useEffect(() => {
        if (itemToEdit) {
            setEditTitle(itemToEdit.title);
            setEditDescription(itemToEdit.description);
            setEditLink(itemToEdit.link);
            setEditVideoLength(itemToEdit.video_length);
            setSelectedCategory(
                createCategorySelectObject(itemToEdit.category_id))
        }
    }, [itemToEdit])

    // find category label from provided category id then return {value: id, label : category_name}
    function createCategorySelectObject(categoryId) {
        return categoryOptions
            .filter((category) =>
                (category.id === categoryId))
            .map(categoryMatch =>
                ({ value: categoryMatch.id, label: categoryMatch.category_name }))
        [0]
    }

    // clear edit modal
    function handlEditModalClose() {
        setShowEditModal(false);
        setItemToEdit({})
        setEditTitle("")
        setEditDescription("")
        setEditLink("")
        setEditVideoLength("")
        setSelectedCategory({})
        setFile(null)
        setUseFile(false)
    }

    const {processUpload} = useS3DirectUpload()

    // submit edit modal data
    async function handleEditResource() {
        let link = editLink
        if(useFile&&file){
            const s3Res = await processUpload(file)
            link = s3Res.payload.url
        }
        statusDispatch(editExternalResource(
            {
                id: itemToEdit.id,
                title: editTitle,
                description: editDescription,
                link,
                video_length: editVideoLength,
                category_id: selectedCategory ? selectedCategory.value : 0,
                // added for redux editExternalResource.pending update only, not sent to api
                category_name: selectedCategory ? selectedCategory.label : "none"
            }
        ), "resource", "update")

        handlEditModalClose();
    }

    return (
        <Modal show={showEditModal} onHide={handlEditModalClose}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Resource </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label> Title  </Form.Label>
                    <Form.Control value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)} />
                </Form.Group>

                <Form.Group>
                    <Form.Label> Description  </Form.Label>
                    <Form.Control value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)} />
                </Form.Group>
                <Form.Group>
                    <Form.Label> Category  </Form.Label>
                    <CategorySelect
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                    />
                </Form.Group>
                {!useFile&&
                    <Form.Group>
                        <Form.Label > Link  </Form.Label>
                        <Form.Control value={editLink}
                            onChange={(e) => setEditLink(e.target.value)} />
                    </Form.Group>
                }
                {(useFile&&!Boolean(file))&&
                    <Form.Group>
                        <Form.Label>
                            File
                        </Form.Label>
                        <Form.File 
                            onChange={e=>{
                                setFile(e.target.files[0])
                            }}
                            id="add-resource-file" 
                            label="Choose file" 
                            custom 
                        />
                    </Form.Group>
                }
                {(useFile&&Boolean(file))&&
                    <div className="d-flex justify-content-between">
                        {file.name}
                        <Button 
                            onClick={setFile.bind(null,null)}
                            variant="outline-danger"
                            size="sm"
                        >
                            Clear File
                        </Button>
                    </div>
                }
                <Form.Check
                    className="mb-2" 
                    onChange={e=>{
                        setUseFile(e.target.checked)
                    }}
                    id="add-resource-file-check" 
                    label="Upload File"
                    checked={useFile}
                />
                <Form.Group>
                    <Form.Label> Video Length (optional)  </Form.Label>
                    <Form.Control value={editVideoLength}
                        onChange={(e) => setEditVideoLength(e.target.value)} />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={handleEditResource}>
                    Edit Resource
                </Button>
            </Modal.Footer>
        </Modal>
    )
}


// ********** ADD RESOURCE MODAL ***************

function AddExternalResourceModal({
    showAddModal,
    setShowAddModal
}) {

    // add modal state
    const [addTitle, setAddTitle] = useState("");
    const [addDescription, setAddDescription] = useState("");
    const [addLink, setAddLink] = useState("");
    const [addVideoLength, setAddVideoLength] = useState("");
    const [isCreateLoading, setIsCreateLoading] = useState(false);
    const [useFile, setUseFile] = useState(false);
    const [file,setFile] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)

    const {processUpload} = useS3DirectUpload()


    const dispatch = useDispatch();

    const statusDispatch = useApiStatusDispatch();

    //clear add modal
    function handleAddModalClose() {
        setIsCreateLoading(false)
        setShowAddModal(false);
        setAddTitle("");
        setAddDescription("");
        setAddLink("");
        setAddVideoLength("");
        setSelectedCategory(null)
    }

    //submit add modal data
    async function handleSubmitResource() {
        setIsCreateLoading(true);
        let link = addLink
        if(useFile&&file){
            const s3Res = await processUpload(file)
            link = s3Res.payload.url
        }
        statusDispatch(createExternalResource(
            {
                title: addTitle,
                description: addDescription,
                link,
                video_length: addVideoLength,
                category_id: selectedCategory ? selectedCategory.value : 0
            }
        ), "resource", "create")
            .then(
                res => {
                    handleAddModalClose()
                    setFile(null)
                    setUseFile(false)
                }
            )
    }



    return (
        <Modal show={showAddModal} onHide={handleAddModalClose}>
            <Modal.Header closeButton>
                <Modal.Title>Add Resource </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label> Title  </Form.Label>
                    <Form.Control value={addTitle}
                        onChange={(e) => setAddTitle(e.target.value)} />
                </Form.Group>

                <Form.Group>
                    <Form.Label> Description  </Form.Label>
                    <Form.Control value={addDescription}
                        onChange={(e) => setAddDescription(e.target.value)} />
                </Form.Group>
                <Form.Group>
                    <Form.Label> Category  </Form.Label>
                    <CategorySelect
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                    />
                </Form.Group>
                {!useFile&&
                    <Form.Group>
                        <Form.Label>
                            Link
                        </Form.Label>
                        <Form.Control value={addLink}
                            onChange={(e) => setAddLink(e.target.value)} />
                    </Form.Group>
                }
                {(useFile&&!Boolean(file))&&
                    <Form.Group>
                        <Form.Label>
                            File
                        </Form.Label>
                        <Form.File 
                            onChange={e=>{
                                setFile(e.target.files[0])
                            }}
                            id="add-resource-file" 
                            label="Choose file" 
                            custom 
                        />
                    </Form.Group>
                }
                {(useFile&&Boolean(file))&&
                    <div className="d-flex justify-content-between">
                        {file.name}
                        <Button 
                            onClick={setFile.bind(null,null)}
                            variant="outline-danger"
                            size="sm"
                        >
                            Clear File
                        </Button>
                    </div>
                }
                <Form.Check
                    className="mb-2" 
                    onChange={e=>{
                        setUseFile(e.target.checked)
                    }}
                    id="add-resource-file-check" 
                    label="Upload File"
                    checked={useFile}
                />

                <Form.Group>
                    <Form.Label> Video Length (optional)  </Form.Label>
                    <Form.Control value={addVideoLength}
                        onChange={(e) => setAddVideoLength(e.target.value)} />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    disabled={isCreateLoading}
                    onClick={handleSubmitResource}>
                    {isCreateLoading ?
                        <Spinner size="sm" animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner> : "Create Resource"}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}


export default ExternalResources;